import { Check, ChevronDown } from 'lucide-react'
import { Avatar } from '@/components/design-system'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { agentHueStyle } from '@/frontend/lib/agentHue'
import type { HomeWorker } from './types'

interface Props {
  workers: HomeWorker[]
  target: HomeWorker
  onSelect: (id: string) => void
  align?: 'start' | 'center'
  // Gate opcional (SZ-1583): quando fornecido, agentes que não passam ficam
  // indisponíveis no dropdown (item desabilitado + motivo) e o dot de status
  // reflete a prontidão. Sem ele, todos são selecionáveis (comportamento da
  // home antiga). A página de Nova conversa passa `chat_ready` como gate.
  isReady?: (worker: HomeWorker) => boolean
  unavailableReason?: (worker: HomeWorker) => string
}

/**
 * Seletor de agente explícito (Iteração 1, I1.3): um controle inconfundível no
 * estilo model-picker do Claude — pill média centrada acima do composer com
 * avatar dither (hue do agente) + dot de status, nome e cargo, e chevron
 * abrindo o dropdown com os demais agentes. Resolve "difícil
 * entender qual agente tá selecionado": a resposta deixa de ser um chip pequeno
 * dentro do input. Reusado pela página de Nova conversa (Iteração 3), que passa
 * o gate de prontidão para agentes não-prontos aparecerem como indisponíveis.
 */
export function AgentPicker({ workers, target, onSelect, align = 'center', isReady, unavailableReason }: Props) {
  const firstName = target.name.trim().split(/\s+/)[0]
  const targetReady = isReady ? isReady(target) : true

  return (
    <div className={align === 'start' ? 'flex justify-start' : 'flex justify-center'} style={agentHueStyle(target.id)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-testid="home-agent-picker"
            aria-label={`Conversando com ${target.name} — trocar de agente`}
            className="group inline-flex items-center gap-2.5 rounded-full border border-border bg-card py-1.5 pl-2 pr-3.5 text-left shadow-xs transition-colors duration-150 hover:border-input hover:bg-secondary"
          >
            <span className="relative flex-none">
              <Avatar src={target.avatar_url ?? undefined} alt="" name={target.name} hueSeed={target.id} size="sm" />
              <span
                className={`absolute -bottom-px -right-px h-[9px] w-[9px] rounded-full border-2 border-card ${
                  targetReady ? 'bg-success' : 'bg-faint-foreground'
                }`}
                aria-hidden
              />
            </span>
            <span className="leading-tight">
              <span className="block text-[13.5px] font-semibold tracking-[-0.01em] text-foreground">
                {firstName}
              </span>
              <span className="block text-[11px] text-muted-foreground">
                {target.job_title || 'Colaborador digital'}
              </span>
            </span>
            <ChevronDown
              className="h-3.5 w-3.5 flex-none text-faint-foreground transition-colors duration-150 group-hover:text-muted-foreground"
              strokeWidth={2}
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="min-w-[240px]">
          {workers.map((worker) => {
            const ready = isReady ? isReady(worker) : true
            const reason = !ready && unavailableReason ? unavailableReason(worker) : null
            return (
              <DropdownMenuItem
                key={worker.id}
                disabled={!ready}
                onSelect={() => onSelect(worker.id)}
                style={agentHueStyle(worker.id)}
                data-testid={`home-agent-option-${worker.id}`}
                className="gap-2.5 py-2"
              >
                <Avatar src={worker.avatar_url ?? undefined} alt="" name={worker.name} hueSeed={worker.id} size="xs" className="flex-none" />
                <span className="min-w-0 flex-1 leading-tight">
                  <span className="block truncate text-[13px] font-medium text-foreground">{worker.name}</span>
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {reason || worker.job_title || 'Colaborador digital'}
                  </span>
                </span>
                {worker.id === target.id && ready && (
                  <Check className="h-3.5 w-3.5 flex-none text-foreground" strokeWidth={2} />
                )}
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
